import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {Component} from '@angular/core';
import {AuthSessionApiService} from '@npck/auth-frontend-api';
import {AnalyticsService} from '@npck/libs/analytics-sdk';
import {
    addSentryErrorLog,
    BusinessErrorCode,
    ERROR_PARAM,
    extractRequestId,
    parseUUID,
    RouterService,
    SentryService,
    toBusinessOrNull,
} from '@npck/libs/commons';
import {AuthHolderService} from '../../domain/auth-holder.service';
import {AUTH_INIT, AUTH_STARTED} from '../../infra/analytics-events';
import {Error} from '../../infra/app-error-handler.service';
import {Path} from '../../path';

@Component({
    template: `
        <div class="w-100 h-100" fxLayoutAlign="center center">
            <ui-kit-spinner></ui-kit-spinner>
        </div>
    `,
    standalone: false
})
export class OauthComponent {

    constructor(
        private routerService: RouterService,
        private analyticsService: AnalyticsService,
        private sentryService: SentryService,
        oauthHolderService: AuthHolderService,
        authSessionApiService: AuthSessionApiService,
    ) {
        addSentryErrorLog('OauthComponent init started');
        const params = new URL(window.location.href).searchParams;
        const oauthSessionId = parseUUID(params.get('session-id'));
        if (!oauthSessionId) {
            this.navigateToError();
            return;
        }
        this.analyticsService.finishSession();
        oauthHolderService.clear();
        oauthHolderService.storeOauthSessionId(oauthSessionId);
        analyticsService.capture(AUTH_INIT);
        authSessionApiService.authSessionGetData({oauthSessionId})
            .subscribe({
                next: response => {
                    oauthHolderService.storeFirstFactorToken(response.firstFactorToken);
                    oauthHolderService.storeOauthSessionData(response);
                    this.analyticsService.setUserId(response.iinEncrypted);
                    analyticsService.capture(AUTH_STARTED);
                    if (response.revokeEsign) {
                        routerService.navigate(Path.LIVENESS);
                    } else {
                        routerService.navigate(Path.IIN);
                    }
                },
                error: (error: HttpErrorResponse) => {
                    if (toBusinessOrNull(error)?.error === BusinessErrorCode.AUTH_SESSION_ALREADY_COMPLETED) {
                        this.navigateToSessionCompleted();
                    } else if (error.status === HttpStatusCode.Unauthorized) {
                        this.navigateToError(error);
                    } else {
                        throw error;
                    }
                },
            });
        addSentryErrorLog('OauthComponent init finished');
    }

    private navigateToSessionCompleted(): void {
        this.routerService.navigate(Path.ERROR, {queryParams: {[ERROR_PARAM]: Error.SESSION_COMPLETED}});
    }

    private navigateToError(error?: HttpErrorResponse): void {
        const errorCode = Error.INVALID_LINK;
        // TODO sessionId is required this code breaks sending to analytics
        // this.analyticsService.capture(AUTH_ERROR, {reason: errorCode, sessionId: 'no sessionId'});
        this.sentryService.sendErrorMessage('Invalid sessionId', scope => {
            scope.setTag('requestId', extractRequestId(error));
            scope.setTag('sessionId', sessionStorage.getItem('preloaderSessionId') || null);
            scope.addBreadcrumb({
                data: {
                    errorMessage: error?.message,
                    errorStatus: error?.status,
                },
            });
        });
        this.routerService.navigate(Path.ERROR, {
            queryParams: {
                [ERROR_PARAM]: errorCode,
                requestId: extractRequestId(error),
            },
        });
    }
}
